import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import { FaExclamationTriangle } from 'react-icons/fa';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f1419 0%, #1a1f2e 50%, #0f1419 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: rgba(26, 31, 46, 0.95);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 20px;
  padding: 48px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  text-align: center;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const LogoText = styled.h1`
  font-size: 36px;
  font-weight: 700;
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 28px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #3a4055;
  border-top: 3px solid #8b5cf6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SessionExpiredBanner = styled.div`
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.4);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BannerIcon = styled.span`
  font-size: 20px;
  color: #fbbf24;
  display: flex;
  align-items: center;
`;

const BannerText = styled.div`
  color: #fbbf24;
  font-size: 14px;
  font-weight: 500;
`;

/**
 * TeamLoginPage - Simple redirect to Auth0
 * 
 * The new flow is:
 * 1. Auth0 login first (company level)
 * 2. If team members exist -> member login page
 * 3. If no team members -> full access
 * 
 * This page just redirects to Auth0 automatically
 */
export default function TeamLoginPage() {
  const { loginWithRedirect, isAuthenticated, isLoading, error } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  
  useEffect(() => {
    // Check if redirected due to session expiration
    const params = new URLSearchParams(location.search);
    if (params.get('expired') === 'true') {
      setShowExpiredMessage(true);
    }
  }, [location.search]);
  
  useEffect(() => {
    // Handle redirect logic
    let timeoutId;
    
    if (!isLoading && !redirectAttempted) {
      const params = new URLSearchParams(location.search);
      const returnTo = params.get('returnTo') || '/';
      const isExpired = params.get('expired') === 'true';
      
      // If session expired, wait 2 seconds so user can see the message
      const delay = isExpired ? 2000 : 0;
      
      if (isAuthenticated) {
        // Already authenticated with Auth0 - redirect to member login or main page
        // The team member session expired, not Auth0 session
        timeoutId = setTimeout(() => {
          setRedirectAttempted(true);
          // Navigate to member login to re-authenticate team member
          navigate('/member-login', { replace: true });
        }, delay);
      } else {
        // Not authenticated - redirect to Auth0
        timeoutId = setTimeout(async () => {
          try {
            setRedirectAttempted(true);
            await loginWithRedirect({
              appState: {
                returnTo: returnTo,
              },
            });
          } catch (err) {
            console.error('Auth0 loginWithRedirect failed:', err);
            window.location.href = '/';
          }
        }, delay);
      }
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAuthenticated, isLoading, loginWithRedirect, location, redirectAttempted, navigate]);

  // If already authenticated, redirect will happen via ProtectedRoute
  // If not, show loading while Auth0 redirect happens
  return (
    <PageContainer>
      <Card>
        <Logo>
          <LogoText>iMaven</LogoText>
        </Logo>
        
        {showExpiredMessage && (
          <SessionExpiredBanner>
            <BannerIcon><FaExclamationTriangle /></BannerIcon>
            <BannerText>Your session has expired. Please log in again.</BannerText>
          </SessionExpiredBanner>
        )}
        
        <Spinner />
        <Title>{showExpiredMessage ? 'Session Expired' : 'Signing you in...'}</Title>
        <Subtitle>
          {showExpiredMessage 
            ? 'Redirecting to secure login...' 
            : 'Redirecting to secure login'
          }
        </Subtitle>
      </Card>
    </PageContainer>
  );
}
